// 9 Oct: proving a returning player's address from the game (Play) and the result page. Given the player's session and
// the latest /me: while the server offers proof (returning.known, not verified) and a signed-in session on this device
// can prove the address (lib/proof's proofSession), it posts /battles/{code}/verify once per sign-in with the sign-in's
// ID token, reads /me again and hands the fresh answer to `onProved`. A refusal (any other 4xx) is final for that
// sign-in; a throttle (429), a server error or network trouble is tried again at the next read of /me. The seat having
// moved to another device (401 seat-moved, lib/seat) is marked on the game store for the page to act on. Signing out
// stops it; a new sign-in is tried afresh. Nothing here logs.
import { useEffect, useRef } from 'react';
import type { MeResponse } from '@shared/types';
import { api, ApiError } from './api';
import { offersProof, proofSession, verifyPath } from './proof';
import { isSeatMoved } from './seat';
import type { Session } from './session';
import { useStaffSession } from '@/staff/session';
import { useGame } from '@/store/game';

/** Whether a failed proof should be tried again with the same sign-in: not for a refusal, yes for a throttle, a server error or no answer. */
export const retryable = (e: unknown): boolean => !(e instanceof ApiError) || e.status === 429 || e.status >= 500;

export function useProof({ session, me, onProved, mePath }: {
  session: Session | undefined; me: MeResponse | undefined;
  /** The fresh /me after the proof, for the page to take. */
  onProved: (m: MeResponse) => void;
  /** The /me path to read back (the game asks for nothing older than the snapshot it holds); default '/me'. */
  mePath?: () => string;
}): void {
  const account = useStaffSession();
  const offered = useRef<string>(undefined);
  const proved = useRef(onProved); proved.current = onProved;
  const path = useRef(mePath); path.current = mePath;
  useEffect(() => {
    const proof = proofSession(account);
    if (!session || !proof || !offersProof(me) || offered.current === proof.token) return;
    offered.current = proof.token;
    const s = session;
    api(verifyPath(s.code), { method: 'POST', token: s.token, body: JSON.stringify({ idToken: proof.token }) })
      .then(() => api<MeResponse>(path.current?.() ?? '/me', { token: s.token }))
      .then((m) => { if (m.playerId === s.playerId) proved.current(m); })
      .catch((e) => { if (isSeatMoved(e)) useGame.getState().markSeatMoved(); else if (retryable(e)) offered.current = undefined; });
  }, [account, me, session]);
}
